import { Injectable, inject } from '@angular/core';
import { of, race, Subject, timer } from 'rxjs';
import { delay, finalize, map, tap } from 'rxjs/operators';

import { ModalClosedReason, ModalContainer, ModalOptions, ApModalService } from '@ap/modal';

import { ApLoaderModalComponent } from './loader-modal.component';
import { LoaderModalOptions } from './loader-modal.model';

@Injectable({
    providedIn: 'root'
})
export class ApLoaderModalService {
    private readonly modalService = inject(ApModalService);

    /**
     * Show the loading spinner for x ms longer; default = 750;
     */
    private readonly LOADING_DELAY = 750;
    /**
     * Show the loading animation for x ms longer; default = 1000;
     */
    private readonly READY_DELAY = 1000;
    /**
     * Show spinner after a certain time in ms; default = 0;
     */
    private readonly SPINNER_DELAY = 0;

    /**
     *
     * @param loadingText = the text you want when the modal is loading
     * @param readyText  = the text when the modal is ready
     * @param showSpinnerDelay = give in an number if you want that the modal opens later.
     * @param loadingDelay = give in an number if you want delay the loading.
     * @param readyDelay = give in an number if you want to delay the modal when it is ready.
     * @returns
     */
    public openLoader(
        loadingText: string,
        readyText: string,
        showSpinnerDelay: number = this.SPINNER_DELAY,
        loadingDelay: number = this.LOADING_DELAY,
        readyDelay: number = this.READY_DELAY
    ): Subject<ModalClosedReason> {
        const loaderModalOptions: LoaderModalOptions = {
            loadingText,
            readyText,
            readySubject: new Subject<ModalClosedReason>()
        };
        const loadingFinished = new Subject<ModalClosedReason>();
        const modalOptions = new ModalOptions(loaderModalOptions);
        modalOptions.cancelable = false;
        modalOptions.showHeader = false;
        modalOptions.fullscreen = true;
        modalOptions.classIdentifier = 'ap-modal__dialog--transparent';

        race(loadingFinished.pipe(map(() => false)), timer(showSpinnerDelay).pipe(map(() => true))).subscribe((result) => {
            if (result) {
                const modalContainer: ModalContainer = this.modalService.open(ApLoaderModalComponent, modalOptions);

                const openTimeInMs = Date.now();

                loadingFinished.subscribe((reason) => {
                    of(reason)
                        .pipe(
                            delay(this.getDelayUntilReady(openTimeInMs, loadingDelay)),
                            tap(() => loaderModalOptions.readySubject.next(reason)),
                            delay(reason === ModalClosedReason.ACCEPTED ? readyDelay : 0),
                            finalize(() => {
                                modalContainer.close(reason);
                                loadingFinished.complete();
                            })
                        )
                        .subscribe();
                });
            } else {
                loadingFinished.complete();
            }
        });
        return loadingFinished;
    }

    /**
     *
     * @param openTimeInMs
     * @param loadingDelay
     * @returns
     */
    private getDelayUntilReady(openTimeInMs: number, loadingDelay?: number): number {
        const nowInMs = Date.now();
        const passedMs = nowInMs - openTimeInMs;
        return Math.max(loadingDelay - passedMs, 0);
    }
}
